import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import Link from 'next/link';
import {useTranslation} from 'react-i18next';

import {ArrowBackIcon, LittleWalksLogo, MoreVertIcon, SettingsIcon} from 'Images/icons';

/**
 * The props for the `AppHeader` component.
 * Configures the top navigation bar with optional back and more actions.
 */
interface ComponentProps {
    /** Whether to show the back arrow on the left. */
    isBackVisible?: boolean;
    /** Whether to show the more icon on the right. */
    isMoreVisible?: boolean;
    /** Callback when the back button is pressed. */
    onBack?(): void;
    /** Callback when the more button is pressed. */
    onMore?(): void;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'AppHeader'
     */
    testId?: string;
}

/**
 * The top application header bar with centered logo, optional back arrow, and more icon.
 * Matches the Figma AppHeader at full-width × 64px height, surfBg background.
 *
 * @param props               The component props.
 * @param props.isBackVisible Whether to show the back arrow. Defaults to `false`.
 * @param props.isMoreVisible Whether to show the more icon. Defaults to `false`.
 * @param props.onBack        Callback for the back button press.
 * @param props.onMore        Callback for the more button press.
 * @param props.testId        An optional test ID used for automation selectors. Defaults to `'AppHeader'`.
 * @returns A JSX element rendering the AppHeader.
 *
 * @example
 * ```tsx
 * <AppHeader isBackVisible onBack={handleBack} isMoreVisible onMore={handleMore} />
 * ```
 */
const AppHeader = ({
    isBackVisible = false,
    isMoreVisible = false,
    onBack,
    onMore,
    testId = 'AppHeader'
}: ComponentProps) => {
    const {t} = useTranslation();

    return (
        <Header data-cy={testId}>
            <Side>
                {isBackVisible && (
                    <IconButton
                        aria-label={t('common.back')}
                        data-cy={`${testId}-back`}
                        type="button"
                        onClick={onBack}
                    >
                        <ArrowBackIcon height={24} width={24} />
                    </IconButton>
                )}
            </Side>
            <LogoLink aria-label={t('common.logoHomeLink')} data-cy={`${testId}-logo`} href="/">
                <LittleWalksLogo height={30} />
            </LogoLink>
            <Side $align="end">
                <SettingsLink aria-label={t('nav.settings')} data-cy={`${testId}-settings`} href="/settings">
                    <SettingsIcon height={24} width={24} />
                </SettingsLink>
                {isMoreVisible && (
                    <IconButton
                        aria-label={t('common.moreOptions')}
                        data-cy={`${testId}-more`}
                        type="button"
                        onClick={onMore}
                    >
                        <MoreVertIcon height={24} width={24} />
                    </IconButton>
                )}
            </Side>
        </Header>
    );
};

AppHeader.displayName = 'AppHeader';

export {AppHeader};

// ── Styled ──────────────────────────────────────────────────

const Header = styled.header`
    align-items: center;
    background: ${({theme}) => theme.colors.surfaceBackground};
    display: flex;
    height: 6.4rem;
    justify-content: space-between;
    padding: 0 ${spacing(4)};
    width: 100%;
`;

const Side = styled.div<{$align?: 'end'}>`
    align-items: center;
    display: flex;
    flex: 1;
    justify-content: ${({$align}) => ($align === 'end' ? 'flex-end' : 'flex-start')};
    min-width: 4rem;
`;

const IconButton = styled.button`
    align-items: center;
    background: none;
    border: none;
    color: ${({theme}) => theme.colors.inkPrimary};
    cursor: pointer;
    display: flex;
    height: 4rem;
    justify-content: center;
    margin: 0;
    padding: 0;
    width: 4rem;
`;

const LogoLink = styled(Link)`
    color: ${({theme}) => theme.colors.inkPrimary};
    display: flex;
    text-decoration: none;
`;

const SettingsLink = styled(Link)`
    align-items: center;
    color: ${({theme}) => theme.colors.inkTertiary};
    display: flex;
    height: 4rem;
    justify-content: center;
    text-decoration: none;
    width: 4rem;
`;